/* eslint-disable react/require-default-props */
import React, {
  useRef, useEffect, useState, ReactNode,
} from 'react';
import {
  styled, Box, Icon, IconButton,
} from '@worthy-npm/worthy-common-ui-components';

interface ScrollShadowContainerProps {
  children: ReactNode;
  direction?: 'horizontal' | 'vertical';
  shadowColor?: string;
  shadowSize?: number;
  className?: string;
  showScrollbar?: boolean;
  showScrollArrows?: boolean;
  scrollAmount?: number;
}

const Container = styled(Box, {
  shouldForwardProp: (prop) => ![
    'showLeftShadow',
    'showRightShadow',
    'showTopShadow',
    'showBottomShadow',
    'shadowColor',
    'shadowSize',
    'direction',
  ].includes(prop as string),
})<{
  showLeftShadow: boolean;
  showRightShadow: boolean;
  showTopShadow: boolean;
  showBottomShadow: boolean;
  shadowColor: string;
  shadowSize: number;
  direction: 'horizontal' | 'vertical';
}>(
  ({
    showLeftShadow,
    showRightShadow,
    showTopShadow,
    showBottomShadow,
    shadowColor,
    shadowSize,
    direction,
  }) => ({
    position: 'relative',
    overflow: 'hidden',

    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      zIndex: 1,
      pointerEvents: 'none',
      transition: 'opacity 0.3s ease',
    },

    // Horizontal shadows
    ...(direction === 'horizontal' && {
      '&::before': {
        top: 0,
        left: 0,
        bottom: 0,
        width: shadowSize,
        background: `linear-gradient(90deg, ${shadowColor}, transparent)`,
        opacity: showLeftShadow ? 1 : 0,
      },
      '&::after': {
        top: 0,
        right: 0,
        bottom: 0,
        width: shadowSize,
        background: `linear-gradient(to left, ${shadowColor}, transparent)`,
        opacity: showRightShadow ? 1 : 0,
      },
    }),

    // Vertical shadows
    ...(direction === 'vertical' && {
      '&::before': {
        top: 0,
        left: 0,
        right: 0,
        height: shadowSize,
        background: `linear-gradient(to bottom, ${shadowColor}, transparent)`,
        opacity: showTopShadow ? 1 : 0,
      },
      '&::after': {
        bottom: 0,
        left: 0,
        right: 0,
        height: shadowSize,
        background: `linear-gradient(to top, ${shadowColor}, transparent)`,
        opacity: showBottomShadow ? 1 : 0,
      },
    }),
  }),
);

const ScrollableContent = styled('div')<{
  direction: 'horizontal' | 'vertical';
  showScrollbar: boolean;
}>(({ direction, showScrollbar }) => ({
  ...(direction === 'horizontal' && {
    overflowX: 'auto',
    overflowY: 'hidden',
  }),
  ...(direction === 'vertical' && {
    overflowY: 'auto',
    overflowX: 'hidden',
  }),
  ...(!showScrollbar && {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  }),
  padding: showScrollbar ? 8 : 0,
}));

const ArrowButtonsContainer = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 2,
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
}));

const ScrollArrowButton = styled(IconButton)<{ shadowColor: string }>(({ shadowColor }) => ({
  width: 32,
  height: 32,
  background: `radial-gradient(circle at center, ${shadowColor}, transparent 70%)`,
  '&:hover': {
    background: `radial-gradient(circle at center, ${shadowColor}, transparent 70%)`,
  },
  '&:disabled': {
    background: `radial-gradient(circle at center, ${shadowColor.replace(
      ')',
      ', 0.5)',
    )}, transparent 70%)`,
    opacity: 0.5,
  },
}));

// eslint-disable-next-line react/display-name
const ScrollShadowContainer = React.forwardRef<HTMLDivElement, ScrollShadowContainerProps>(
  (
    {
      children,
      direction = 'horizontal',
      shadowColor = 'rgba(0, 0, 0, 0.1)',
      shadowSize = 20,
      className,
      showScrollbar = false,
      showScrollArrows = true,
      scrollAmount = 200,
    },
    ref,
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showLeftShadow, setShowLeftShadow] = useState(false);
    const [showRightShadow, setShowRightShadow] = useState(false);
    const [showTopShadow, setShowTopShadow] = useState(false);
    const [showBottomShadow, setShowBottomShadow] = useState(false);

    const updateShadows = () => {
      const element = scrollRef.current;
      if (!element) return;

      if (direction === 'horizontal') {
        const { scrollLeft, scrollWidth, clientWidth } = element;

        setShowLeftShadow(scrollLeft > 0);
        setShowRightShadow(scrollLeft < scrollWidth - clientWidth);
      } else {
        const { scrollTop, scrollHeight, clientHeight } = element;

        setShowTopShadow(scrollTop > 0);
        setShowBottomShadow(scrollTop < scrollHeight - clientHeight);
      }
    };

    const scrollLeft = () => {
      const element = scrollRef.current;
      if (!element) return;
      element.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    };

    const scrollRight = () => {
      const element = scrollRef.current;
      if (!element) return;
      element.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    useEffect(() => {
      const element = scrollRef.current;
      if (!element) return undefined;

      let rafId: number;

      // Function to check and update shadows after browser paint with double RAF
      const checkShadows = () => {
        if (typeof requestAnimationFrame !== 'undefined') {
          requestAnimationFrame(() => {
            rafId = requestAnimationFrame(() => {
              updateShadows();
            });
          });
        } else {
          setTimeout(() => {
            setTimeout(updateShadows, 16);
          }, 16); // Double timeout fallback
        }
      };

      // Initial check after content renders
      checkShadows();

      // Add scroll listener
      element.addEventListener('scroll', updateShadows);

      // Add resize observer to handle content changes (with polyfill check)
      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(updateShadows);
        resizeObserver.observe(element);
      }

      const cleanup = () => {
        if (rafId && typeof cancelAnimationFrame !== 'undefined') {
          cancelAnimationFrame(rafId);
        }
        element.removeEventListener('scroll', updateShadows);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };

      return cleanup;
    }, [direction, children]);

    return (
      <Container
        ref={ref}
        className={className}
        showLeftShadow={showLeftShadow}
        showRightShadow={showRightShadow}
        showTopShadow={showTopShadow}
        showBottomShadow={showBottomShadow}
        shadowColor={shadowColor}
        shadowSize={shadowSize}
        direction={direction}
      >
        {showScrollArrows && direction === 'horizontal' && (
          <ArrowButtonsContainer>
            <ScrollArrowButton
              onClick={scrollLeft}
              disabled={!showLeftShadow}
              shadowColor={shadowColor}
            >
              <Icon.ExpandMoreShineIcon sx={{ fontSize: 16, transform: 'rotate(90deg)' }} />
            </ScrollArrowButton>
            <ScrollArrowButton
              onClick={scrollRight}
              disabled={!showRightShadow}
              shadowColor={shadowColor}
            >
              <Icon.ExpandMoreShineIcon sx={{ fontSize: 16, transform: 'rotate(270deg)' }} />
            </ScrollArrowButton>
          </ArrowButtonsContainer>
        )}
        <ScrollableContent ref={scrollRef} direction={direction} showScrollbar={showScrollbar}>
          {children}
        </ScrollableContent>
      </Container>
    );
  },
);

export default ScrollShadowContainer;
